(function () {
  let pendingBfcacheReset = false;

  window.addEventListener('pageshow', (event) => {
    const navEntry = performance.getEntriesByType('navigation')[0];
    if (event.persisted || navEntry?.type === 'back_forward') {
      pendingBfcacheReset = true;
    }
  });

  function formatMoneyFromCents(cents, template) {
    const amount = Number(cents) / 100;
    if (template && window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.money_format || window.theme?.moneyFormat);
    }
    if (template) {
      const match = template.match(/^([^\d]*)([\d,]+(?:\.\d+)?)/);
      if (match) {
        return `${match[1]}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    }
    return `$${amount.toFixed(2)}`;
  }

  function base64UrlEncode(value) {
    const bytes = new TextEncoder().encode(value);
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  function getPicker(productForm) {
    return productForm?.closest('product-info')?.querySelector(
      `xlean-deposit-payment[data-section-id="${productForm.dataset.sectionId}"]`
    );
  }

  async function hasMatchingDepositInCart(tr1VariantId) {
    const cartUrl = window.routes?.cart_url || '/cart';
    const cart = await fetch(`${cartUrl}.js`).then((response) => response.json());
    return (cart.items || []).some(
      (item) =>
        item.properties?.['Payment Type'] === 'Deposit' &&
        String(item.properties?.['_TR1 Variant ID']) === String(tr1VariantId)
    );
  }

  window.xleanDepositPayment = {
    async forProductForm(productForm) {
      const picker = getPicker(productForm);
      if (!picker) return null;
      await picker.ensureReady();
      if (picker.isDepositMode() && !picker.depositAvailable) {
        return {
          abort: true,
          error: 'Deposit payment is currently unavailable. Please choose full payment or contact support.',
        };
      }
      if (!picker.validateAddToCart()) return { abort: true };
      if (picker.isDepositMode()) {
        try {
          if (picker.tr1Variant?.id && (await hasMatchingDepositInCart(picker.tr1Variant.id))) {
            return { abort: true, error: 'This deposit product is already in your cart.' };
          }
        } catch (error) {
          console.error('[xlean-deposit-payment] Unable to check the cart.', error);
        }
        return { formData: picker.buildAddToCartFormData() };
      }
      return null;
    },
  };

  class XleanDepositPayment extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.dataset.sectionId;
      this.depositVariantId = this.dataset.depositVariantId;
      this.depositAmountCents = Number(this.dataset.depositAmountCents || 0);
      this.depositPriceHtml = this.dataset.depositPriceHtml || '';
      this.depositAvailable = this.dataset.depositAvailable === 'true';
      this.paymentMethod = 'full';
      this.tr1Variant = null;
      this.originalPriceHtml = null;

      this.productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
      this.form = document.getElementById(`product-form-${this.sectionId}`);
      this.checkoutLink = document.getElementById(`ProductCheckoutLink-${this.sectionId}`);
      this.priceContainer = document.getElementById(`price-${this.sectionId}`);
      this.memberIdField = this.form?.querySelector('[data-xlean-member-id-input]');
      this.selectedValueEl = this.querySelector('.form__label [data-selected-value]');
      this.balanceNoteEl = this.querySelector('[data-payment-balance-note]');
      this.depositNoteEl = this.querySelector('[data-payment-deposit-note]');

      this.ensureReady().then(() => this.applyPaymentMethod());
      this.bindEvents();
      this.cacheOriginalPrice();
      this.syncFromVariantSelects();
      this.applyPaymentMethodFromUrl();
    }

    ensureReady() {
      if (!this._readyPromise) {
        this._readyPromise = Promise.resolve();
      }
      return this._readyPromise;
    }

    bindEvents() {
      this.querySelectorAll('[data-payment-method-input]').forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.paymentMethod = input.value;
          this.applyPaymentMethod();
        });
      });

      if (this.checkoutLink) {
        this.checkoutLink.addEventListener('click', async (event) => {
          await this.ensureReady();
          if (!this.validateAddToCart()) {
            event.preventDefault();
            return;
          }
          if (this.isDepositMode()) {
            event.preventDefault();
            if (!this.depositVariantId) return;
            window.location.href = this.buildDepositCheckoutUrl();
          }
        });
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || String(data.sectionId) !== String(this.sectionId)) return;
          if (pendingBfcacheReset) {
            pendingBfcacheReset = false;
            this.resetToDefault();
            return;
          }
          this.onVariantChange(data.variant);
          window.requestAnimationFrame(() => this.refreshPriceDisplay());
        });

        this._optionChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
          if (!data?.event?.target || !this.productInfo?.contains(data.event.target)) return;
          window.setTimeout(() => this.syncFromVariantSelects(), 0);
        });
      }
    }

    disconnectedCallback() {
      this._variantChangeUnsubscriber?.();
      this._optionChangeUnsubscriber?.();
    }

    resetToDefault() {
      this.paymentMethod = 'full';
      this.querySelectorAll('[data-payment-method-input]').forEach((input) => {
        input.checked = input.value === 'full';
      });
      window.xleanMemberVerification?.ensureMemberId?.();
      this.applyPaymentMethod();
    }

    isDepositMode() {
      return this.paymentMethod === 'deposit';
    }

    applyPaymentMethodFromUrl() {
      const payment = new URLSearchParams(window.location.search).get('payment');
      if (payment !== 'deposit') return;
      const depositInput = this.querySelector('[data-payment-method-input][value="deposit"]');
      if (!depositInput || depositInput.disabled || !this.depositAvailable) return;
      depositInput.checked = true;
      this.paymentMethod = 'deposit';
    }

    validateAddToCart() {
      window.xleanMemberVerification?.ensureMemberId?.();
      if (!this.isDepositMode()) return true;
      if (!this.depositAvailable || !this.depositVariantId) return false;
      if (!this.tr1Variant?.available) return false;
      if (!this.requiresMemberId()) return true;
      if (this.memberIdField?.value?.trim()) return true;
      this.productInfo?.querySelector('.product-form__input--swatch')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    buildAddToCartFormData() {
      const formData = new FormData();
      formData.append('id', this.depositVariantId);
      formData.append('quantity', '1');
      Object.entries(this.getDepositProperties()).forEach(([key, value]) => {
        if (value) formData.append(`properties[${key}]`, value);
      });
      const memberId = this.memberIdField?.value?.trim();
      if (memberId) formData.append('properties[Member ID]', memberId);
      return formData;
    }

    syncFromVariantSelects() {
      const node = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
      if (!node?.textContent) return;
      try {
        this.onVariantChange(JSON.parse(node.textContent));
      } catch (error) {
        console.error(error);
      }
    }

    onVariantChange(variant) {
      this.tr1Variant = variant || null;
      this.applyPaymentMethod();
    }

    cacheOriginalPrice() {
      const regularPrice = this.priceContainer?.querySelector('.price-item--regular');
      if (regularPrice && this.originalPriceHtml === null) {
        this.originalPriceHtml = regularPrice.innerHTML;
      }
    }

    applyPaymentMethod() {
      this.updateCheckoutLink();
      this.updateSelectedLabel();
      this.updatePriceDisplay();
      this.toggleDepositNotes();
    }

    updateSelectedLabel() {
      if (!this.selectedValueEl) return;
      this.selectedValueEl.textContent = this.isDepositMode()
        ? this.dataset.depositLabel || 'Deposit'
        : this.dataset.fullPaymentLabel || 'Full Payment';
    }

    updatePriceDisplay() {
      const regularPrice = this.priceContainer?.querySelector('.price-item--regular');
      if (!regularPrice) return;
      this.cacheOriginalPrice();
      if (this.isDepositMode()) {
        regularPrice.innerHTML = this.depositPriceHtml;
        this.updateBalanceNote();
        this.priceContainer?.classList.add('price--deposit-mode');
        return;
      }
      if (this.originalPriceHtml !== null) regularPrice.innerHTML = this.originalPriceHtml;
      this.priceContainer?.classList.remove('price--deposit-mode');
      this.hideBalanceNote();
    }

    refreshPriceDisplay() {
      if (!this.isDepositMode()) {
        this.cacheOriginalPrice();
        const regularPrice = this.priceContainer?.querySelector('.price-item--regular');
        if (regularPrice && this.originalPriceHtml !== null) regularPrice.innerHTML = this.originalPriceHtml;
        return;
      }
      this.updatePriceDisplay();
    }

    updateBalanceNote() {
      if (!this.balanceNoteEl || !this.tr1Variant?.price) return;
      const balanceCents = Math.max(this.tr1Variant.price - this.depositAmountCents, 0);
      this.balanceNoteEl.textContent = `Balance due before shipping: ${formatMoneyFromCents(balanceCents, this.depositPriceHtml)}`;
      this.balanceNoteEl.hidden = false;
    }

    hideBalanceNote() {
      if (this.balanceNoteEl) this.balanceNoteEl.hidden = true;
    }

    toggleDepositNotes() {
      if (this.depositNoteEl) this.depositNoteEl.hidden = !this.isDepositMode();
    }

    getDepositProperties() {
      const variant = this.tr1Variant;
      if (!variant) return {};
      const balanceCents = Math.max(variant.price - this.depositAmountCents, 0);
      return {
        'Payment Type': 'Deposit',
        '_TR1 Variant ID': String(variant.id),
        Color: variant.option1 || '',
        'Water Supply': variant.option2 || '',
        'Full Price': formatMoneyFromCents(variant.price, this.depositPriceHtml),
        'Balance Due Before Shipment': `${formatMoneyFromCents(balanceCents, this.depositPriceHtml)}`,
      };
    }

    updateCheckoutLink() {
      if (!this.checkoutLink) return;
      if (!this.tr1Variant?.id || !this.tr1Variant.available || (this.isDepositMode() && !this.depositAvailable)) {
        this.checkoutLink.href = '#';
        this.checkoutLink.setAttribute('aria-disabled', 'true');
        return;
      }
      this.checkoutLink.href = this.isDepositMode()
        ? this.buildDepositCheckoutUrl()
        : `${window.routes?.cart_url || '/cart'}/${this.tr1Variant.id}:1?checkout`;
      this.checkoutLink.removeAttribute('aria-disabled');
      document.dispatchEvent(new CustomEvent('xlean:checkout-link-updated'));
    }

    buildDepositCheckoutUrl() {
      const properties = this.getDepositProperties();
      const memberId = this.memberIdField?.value?.trim();
      if (memberId) properties['Member ID'] = memberId;
      const encodedProperties = base64UrlEncode(JSON.stringify(properties));
      return `${window.routes?.cart_url || '/cart'}/${this.depositVariantId}:1?properties=${encodedProperties}&checkout`;
    }

    requiresMemberId() {
      return (this.tr1Variant?.option1 || '').toLowerCase().includes('space silver');
    }
  }

  if (!customElements.get('xlean-deposit-payment')) {
    customElements.define('xlean-deposit-payment', XleanDepositPayment);
  }
})();
